import React from 'react';
import Badge from '../shared/Badge';

interface EnrichedSignalData {
  ticker: string;
  price: number;
  change_pct: number;
  volume: string | number;
  rel_volume: string | number;
  label: string;
  company_name?: string;
  day_range?: string;
  prev_close?: string | number;
  market_cap?: string;
  chart_data?: { time: string; price: number; is_catalyst_event?: boolean }[];
}

interface SignalMetricsPanelProps {
  signalData: EnrichedSignalData;
}

export default function SignalMetricsPanel({ signalData }: SignalMetricsPanelProps) {
  const isPositive = signalData.change_pct >= 0;
  
  const chartPoints = signalData.chart_data && signalData.chart_data.length > 0 
    ? signalData.chart_data 
    : [{ time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), price: signalData.price }];

  const prices = chartPoints.map(p => p.price);
  const minPrice = Math.min(...prices) * 0.99;
  const maxPrice = Math.max(...prices) * 1.01;
  const priceRange = maxPrice - minPrice || 1;

  const width = 600;
  const height = 180;

  const points = chartPoints.map((point, index) => {
    const x = chartPoints.length > 1 ? (index / (chartPoints.length - 1)) * width : width;
    const y = height - ((point.price - minPrice) / priceRange) * height;
    return { x, y, ...point };
  });

  const pathD = points.length > 1 
    ? points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
    : `M 0 ${points[0].y} L ${width} ${points[0].y}`;

  const catalystPoint = points.find(p => p.is_catalyst_event);
  const strokeColor = isPositive ? 'stroke-cat-green' : 'stroke-cat-red';

  return (
    <div className="text-gray-900 dark:text-cat-text-main font-sans">
      <div className="mb-5">
        <div className="text-gray-500 dark:text-cat-text-muted text-[11px] tracking-widest uppercase font-semibold">
          Signal · /signal/{signalData.ticker}
        </div>
        <h1 className="m-0 text-[22px] font-bold mt-1 flex items-baseline gap-2 tracking-tight text-gray-900 dark:text-cat-text-main">
          {signalData.ticker}
          <span className="text-sm text-gray-500 dark:text-cat-text-muted font-normal">
            {signalData.company_name}
          </span>
        </h1>
      </div>

      <div className="bg-white dark:bg-cat-surface border border-gray-200 dark:border-cat-border rounded-2xl overflow-hidden shadow-sm">
        <div className="h-[3px] bg-gradient-to-r from-cat-accent via-cat-accent-2 to-[#a78bfa]" />

        <div className="p-6 flex justify-between items-start">
          <div>
            <div className="text-gray-500 dark:text-cat-text-muted text-[11px] mb-1 uppercase tracking-wider">Last</div>
            <div className="text-4xl font-bold tracking-tight font-mono tabular-nums text-gray-900 dark:text-cat-text-main">
              ${signalData.price.toFixed(2)}
            </div>
            <div className={`text-sm mt-1 font-bold font-mono tabular-nums ${isPositive ? 'text-emerald-600 dark:text-cat-green' : 'text-red-600 dark:text-cat-red'}`}>
              {isPositive ? '+' : ''}{signalData.change_pct}%
            </div>
          </div>

          <div className="text-right">
            <Badge variant={signalData.label as any} className="px-2 py-1">
              {signalData.label}
            </Badge>
          </div>
        </div>

        <div className="px-6 relative">
          <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} className="overflow-visible">
            <line x1={0} y1={height - 20} x2={width} y2={height - 20} className="stroke-gray-300 dark:stroke-cat-border" strokeDasharray="4" />
            <text x={width - 120} y={height - 25} className="fill-gray-500 dark:fill-cat-text-muted text-[10px]">PREV CLOSE ${signalData.prev_close || signalData.price}</text>

            {catalystPoint && (
              <>
                <line x1={catalystPoint.x} y1={0} x2={catalystPoint.x} y2={height} className="stroke-cat-accent" strokeDasharray="3" opacity="0.5" />
                <text x={catalystPoint.x + 8} y={20} className="fill-cat-accent text-[10px]">{catalystPoint.time || '14:05'} · CATALYST</text>
              </>
            )}

            <path d={pathD} fill="none" className={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

            {catalystPoint && (
              <circle cx={catalystPoint.x} cy={catalystPoint.y} r="4" className="fill-white dark:fill-cat-surface stroke-cat-accent" strokeWidth="2" />
            )}
          </svg>
          
          <div className="flex justify-between text-gray-500 dark:text-cat-text-muted text-[10px] font-mono pt-2 pb-4 border-b border-gray-200 dark:border-cat-border">
            <span>{chartPoints.length > 0 ? chartPoints[0].time : '09:30'}</span>
            <span>{chartPoints.length > 2 ? chartPoints[Math.floor(chartPoints.length / 2)].time : '12:00'}</span>
            <span>{chartPoints.length > 1 ? chartPoints[chartPoints.length - 1].time : '14:30'}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 bg-gray-50 dark:bg-cat-bg">
          {[
            { label: 'VOLUME', value: typeof signalData.volume === 'number' ? `${(signalData.volume / 1_000_000).toFixed(1)}M` : signalData.volume },
            { label: 'REL VOLUME', value: typeof signalData.rel_volume === 'number' ? `${signalData.rel_volume.toFixed(1)}x` : signalData.rel_volume },
            { label: 'DAY RANGE', value: signalData.day_range || 'N/A' },
            { label: 'PREV CLOSE', value: signalData.prev_close ? `$${signalData.prev_close}` : 'N/A' },
            { label: 'MARKET CAP', value: signalData.market_cap || 'N/A' },
          ].map((metric, i) => (
            <div key={i} className={`p-4 ${i < 4 ? 'border-r border-gray-200 dark:border-cat-border' : ''}`}>
              <div className="text-gray-500 dark:text-cat-text-muted text-[10px] mb-1.5 font-bold uppercase tracking-wider">{metric.label}</div>
              <div className="text-base font-bold font-mono tabular-nums text-gray-900 dark:text-cat-text-main">{metric.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>  
  );
}
